import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useState } from 'react';

import ChevronIcon from '@/app/assets/svg/arrow-right.svg?react';
import { Logo } from '@/app/components/Header/common/Logo';
import { NavMenu } from '@/app/components/Header/common/NavMenu';
import { TvlInfo } from '@/app/components/Header/common/TvlInfo';
import { ThemeToggle } from '@/app/components/ThemeToggle/ThemeToggle';
import { isServiceMode } from '@/app/shared/constants/env';
import { useBodyLock } from '@/app/shared/hooks/useBodyLock';
import { cn } from '@/app/shared/lib/cn';
import { Button } from '@/app/shared/ui/Button';

export interface MobileHeader {
  tvl: string;
  isLoading: boolean;
}

export const MobileHeader = ({ tvl, isLoading }: MobileHeader) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  useBodyLock(isModalOpen);
  return (
    <div className='xl:hidden'>
      <div className='border-decoration-stroke flex items-center justify-between border-b p-5'>
        <Logo setIsModalOpen={setIsModalOpen} />
        <button
          onClick={() => setIsModalOpen(!isModalOpen)}
          disabled={isServiceMode}
          className='relative z-50 flex h-6 w-6 items-center justify-center disabled:cursor-auto'
          aria-label='Burger menu'
        >
          <div className='relative flex h-4 w-6 flex-col items-center justify-center'>
            <span
              className={`absolute h-0.5 w-6 bg-gray-400 ease-in-out ${
                isModalOpen ? 'translate-y-0 rotate-45' : '-translate-y-1.5'
              }`}
            />
            <span
              className={`absolute h-0.5 w-6 bg-gray-400 ease-in-out ${
                isModalOpen ? 'translate-y-0 -rotate-45' : 'translate-y-1.5'
              }`}
            />
          </div>
        </button>
      </div>

      <div
        className={cn(
          'bg-background fixed right-0 bottom-0 z-50 flex h-[calc(100dvh-var(--mobile-header-height))] w-full flex-col transition-transform duration-300 ease-in-out',
          isModalOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className='border-decoration-stroke border-b px-2 pt-1.5 pb-5'>
          <NavMenu setIsModalOpen={setIsModalOpen} />
        </div>
        <div className='flex flex-col gap-5 p-5'>
          <TvlInfo tvl={tvl} isLoading={isLoading} />
          <div className='flex flex-col gap-2'>
            <ThemeToggle />
            <div className={cn('w-full', isServiceMode && 'pointer-events-none')}>
              <ConnectButton.Custom>
                {({ account, chain, openAccountModal, openConnectModal, authenticationStatus, mounted }) => {
                  const ready = mounted && authenticationStatus !== 'loading';
                  const connected =
                    ready && account && chain && (!authenticationStatus || authenticationStatus === 'authenticated');

                  if (!ready) {
                    return <div className='h-10 w-full animate-pulse rounded bg-gray-800/50' />;
                  }

                  return (
                    <div>
                      {(() => {
                        if (!connected) {
                          return (
                            <Button
                              onClick={openConnectModal}
                              variant='outline'
                              className='h-10 w-full whitespace-nowrap transition-none'
                            >
                              Connect Wallet
                            </Button>
                          );
                        }

                        return (
                          <Button
                            onClick={openAccountModal}
                            variant='ghost'
                            className='bg-gray-800 text-foreground flex h-10 w-full justify-center gap-4 font-medium transition-none'
                          >
                            {account.displayName}
                            <ChevronIcon className='rotate-90' />
                          </Button>
                        );
                      })()}
                    </div>
                  );
                }}
              </ConnectButton.Custom>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
